/**
 * RuleBuilder — visual rule editor for Condition + Switch nodes.
 *
 * No JSONLogic toggle. The editor is the only path into the rule, with one
 * row per comparison and explicit AND/OR groups. Every value input is
 * type-aware:
 *   - boolean   → True / False select
 *   - timestamp → datetime-local picker
 *   - number    → numeric input (or select if the variable has a closed enum)
 *   - string    → text input (or select if the variable has a closed enum)
 *   - "in"      → comma-separated list (placeholder hints the format)
 *
 * If an inbound expression is too complex to express in this grammar (a hand-
 * crafted JSONLogic blob from an import / older flow), we surface a "Reset to
 * default" affordance rather than an opaque editor — keeps every flow editable
 * without exposing JSON.
 */

import { useMemo, useState } from "react";
import { X, Plus, ChevronDown, ChevronRight } from "lucide-react";
import { clsx } from "clsx";
import type { JsonLogicExpression } from "../../types/flow";
import { CONTEXT_VARS, varOptions, varType, varUnitHint, type VarType } from "./data/contextVars";
import { isoToLocalInput, localInputToIso } from "./data/dateUtils";

interface Props {
  value: JsonLogicExpression;
  onChange: (value: JsonLogicExpression) => void;
}

const OPERATORS_BY_TYPE: Record<VarType, { op: string; label: string }[]> = {
  string: [
    { op: "==", label: "equals" },
    { op: "!=", label: "not equals" },
    { op: "in", label: "is in list" },
    { op: "starts_with", label: "starts with" },
    { op: "ends_with", label: "ends with" },
    { op: "regex", label: "matches regex" },
  ],
  number: [
    { op: "==", label: "equals" },
    { op: "!=", label: "not equals" },
    { op: ">", label: ">" },
    { op: "<", label: "<" },
    { op: ">=", label: "≥" },
    { op: "<=", label: "≤" },
  ],
  boolean: [{ op: "==", label: "is" }],
  array: [{ op: "in", label: "contains" }],
  timestamp: [
    { op: ">=", label: "after or on" },
    { op: "<=", label: "before or on" },
    { op: ">", label: "after" },
    { op: "<", label: "before" },
  ],
};

// ─── Visual grammar ──────────────────────────────────────────────────────────

interface Comparison {
  kind: "cmp";
  op: string;
  varName: string;
  value: string;
}

interface Group {
  kind: "group";
  combinator: "and" | "or";
  children: VisualNode[];
}

type VisualNode = Comparison | Group;

function makeEmptyComparison(): Comparison {
  return { kind: "cmp", op: "==", varName: "buyer_country", value: "" };
}

function makeEmptyGroup(combinator: "and" | "or" = "and"): Group {
  return { kind: "group", combinator, children: [makeEmptyComparison()] };
}

// ─── Parse JSONLogic → Visual ───────────────────────────────────────────────

function parse(expr: JsonLogicExpression): VisualNode | null {
  if (!expr || typeof expr !== "object" || Array.isArray(expr)) return null;
  const keys = Object.keys(expr);
  if (keys.length !== 1) return null;
  const [op] = keys;
  const args = (expr as Record<string, JsonLogicExpression>)[op];

  if ((op === "and" || op === "or") && Array.isArray(args)) {
    const children: VisualNode[] = [];
    for (const sub of args) {
      const parsed = parse(sub);
      if (!parsed) return null;
      children.push(parsed);
    }
    return { kind: "group", combinator: op, children };
  }
  return parseComparison(expr);
}

function parseComparison(expr: JsonLogicExpression): Comparison | null {
  if (!expr || typeof expr !== "object" || Array.isArray(expr)) return null;
  const keys = Object.keys(expr);
  if (keys.length !== 1) return null;
  const [op] = keys;
  const args = (expr as Record<string, JsonLogicExpression>)[op];
  if (!Array.isArray(args) || args.length < 2) return null;
  const left = args[0];
  const right = args[1];
  if (typeof left !== "object" || left === null || Array.isArray(left)) return null;
  const leftVar = (left as Record<string, unknown>).var;
  if (typeof leftVar !== "string") return null;
  let valueString: string;
  if (Array.isArray(right)) {
    valueString = right
      .map((v) => (typeof v === "string" || typeof v === "number" ? String(v) : JSON.stringify(v)))
      .join(",");
  } else if (typeof right === "string" || typeof right === "number" || typeof right === "boolean") {
    valueString = String(right);
  } else {
    return null;
  }
  return { kind: "cmp", op, varName: leftVar, value: valueString };
}

// ─── Serialize Visual → JSONLogic ───────────────────────────────────────────

function serialize(node: VisualNode): JsonLogicExpression {
  if (node.kind === "cmp") return serializeComparison(node);
  const children = node.children.map(serialize);
  if (children.length === 0) return true;
  if (children.length === 1) return children[0];
  return { [node.combinator]: children };
}

function serializeComparison(c: Comparison): JsonLogicExpression {
  const varNode: JsonLogicExpression = { var: c.varName };
  if (c.op === "in") {
    const list = c.value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const parsed: JsonLogicExpression[] = list.map((s) => {
      const n = Number(s);
      return Number.isNaN(n) ? s : n;
    });
    return { in: [varNode, parsed] };
  }
  if (c.op === "starts_with" || c.op === "ends_with" || c.op === "regex") {
    return { [c.op]: [varNode, c.value] };
  }
  // Numeric / boolean / generic comparisons
  if (c.value === "true") return { [c.op]: [varNode, true] };
  if (c.value === "false") return { [c.op]: [varNode, false] };
  const n = Number(c.value);
  const rhs: JsonLogicExpression =
    Number.isNaN(n) || c.value.trim() === "" ? c.value : n;
  return { [c.op]: [varNode, rhs] };
}

// ─── Component ───────────────────────────────────────────────────────────────

export function RuleBuilder({ value, onChange }: Props) {
  const parsed = useMemo(() => parse(value), [value]);
  const rootGroup: Group | null = useMemo(() => {
    if (!parsed) return null;
    if (parsed.kind === "group") return parsed;
    return { kind: "group", combinator: "and", children: [parsed] };
  }, [parsed]);

  if (!rootGroup) {
    // Existing rule didn't round-trip into the visual grammar (legacy
    // hand-written JSONLogic). Offer to reset rather than expose JSON.
    return (
      <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-2 text-xs">
        <p className="text-amber-800 dark:text-amber-200">
          This rule was written in a more advanced form than the visual editor
          supports. Reset to a default rule to edit it here?
        </p>
        <button
          type="button"
          onClick={() =>
            onChange(serialize({ kind: "group", combinator: "and", children: [makeEmptyComparison()] }))
          }
          className="rounded border border-amber-400 px-2 py-1 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-900"
        >
          Reset to default rule
        </button>
      </div>
    );
  }

  return (
    <GroupNode
      group={rootGroup}
      isRoot
      onChange={(g) => onChange(serialize(g))}
      onDelete={null}
    />
  );
}

// ─── Group node (recursive) ──────────────────────────────────────────────────

function GroupNode({
  group,
  isRoot,
  onChange,
  onDelete,
}: {
  group: Group;
  isRoot: boolean;
  onChange: (g: Group) => void;
  onDelete: (() => void) | null;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const barColor = group.combinator === "and" ? "bg-blue-500" : "bg-amber-500";
  const labelColor =
    group.combinator === "and"
      ? "text-blue-700 dark:text-blue-300"
      : "text-amber-700 dark:text-amber-300";

  const setChild = (idx: number, child: VisualNode) => {
    const children = [...group.children];
    children[idx] = child;
    onChange({ ...group, children });
  };

  const removeChild = (idx: number) => {
    const children = group.children.filter((_, i) => i !== idx);
    if (isRoot && children.length === 0) {
      onChange({ ...group, children: [makeEmptyComparison()] });
      return;
    }
    if (!isRoot && children.length === 0 && onDelete) {
      onDelete();
      return;
    }
    onChange({ ...group, children });
  };

  return (
    <div
      className={clsx(
        "relative rounded-md border border-neutral-200 dark:border-neutral-700",
        "bg-neutral-50/50 dark:bg-neutral-900/50",
        !isRoot && "ml-1",
      )}
    >
      <div className={clsx("absolute left-0 top-0 bottom-0 w-1 rounded-l-md", barColor)} aria-hidden />
      <div className="pl-3 pr-2 py-2 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-[11px]">
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              className="text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
              aria-label={collapsed ? "Expand group" : "Collapse group"}
            >
              {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            <span className="text-neutral-500">Match</span>
            <select
              value={group.combinator}
              onChange={(e) => onChange({ ...group, combinator: e.target.value as "and" | "or" })}
              className={clsx(
                "rounded-md border border-neutral-200 dark:border-neutral-700",
                "bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-[11px] font-medium",
                labelColor,
              )}
            >
              <option value="and">ALL (AND)</option>
              <option value="or">ANY (OR)</option>
            </select>
            <span className="text-neutral-500">of {group.children.length === 1 ? "this" : "these"}</span>
          </div>
          {!isRoot && onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              className="text-neutral-500 hover:text-category-error"
              aria-label="Remove group"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </div>

        {!collapsed ? (
          <>
            <div className="space-y-1.5">
              {group.children.map((child, idx) => (
                <div key={idx}>
                  {child.kind === "cmp" ? (
                    <ComparisonRow
                      cmp={child}
                      canRemove={group.children.length > 1 || !isRoot}
                      onChange={(c) => setChild(idx, c)}
                      onRemove={() => removeChild(idx)}
                    />
                  ) : (
                    <GroupNode
                      group={child}
                      isRoot={false}
                      onChange={(g) => setChild(idx, g)}
                      onDelete={() => removeChild(idx)}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={() =>
                  onChange({ ...group, children: [...group.children, makeEmptyComparison()] })
                }
                className="inline-flex items-center gap-1 text-[11px] font-medium text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
              >
                <Plus className="w-3 h-3" /> Add clause
              </button>
              <button
                type="button"
                onClick={() =>
                  onChange({
                    ...group,
                    children: [...group.children, makeEmptyGroup(group.combinator === "and" ? "or" : "and")],
                  })
                }
                className="inline-flex items-center gap-1 text-[11px] font-medium text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
              >
                <Plus className="w-3 h-3" /> Add group
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

// ─── Comparison row ──────────────────────────────────────────────────────────

function ComparisonRow({
  cmp,
  canRemove,
  onChange,
  onRemove,
}: {
  cmp: Comparison;
  canRemove: boolean;
  onChange: (c: Comparison) => void;
  onRemove: () => void;
}) {
  // When the var changes, default the operator to one that's valid for the
  // new var's type so we never end up with a stale operator like ">" on a
  // boolean. Reset the value too, since boolean/timestamp values are typed.
  const onChangeVar = (name: string) => {
    const newType = varType(name);
    const validOp = OPERATORS_BY_TYPE[newType][0]?.op ?? "==";
    const opStillValid = OPERATORS_BY_TYPE[newType].some((o) => o.op === cmp.op);
    onChange({
      ...cmp,
      varName: name,
      op: opStillValid ? cmp.op : validOp,
      value: "",
    });
  };

  return (
    <div className="flex items-start gap-1 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2 py-1.5">
      <select
        value={cmp.varName}
        onChange={(e) => onChangeVar(e.target.value)}
        className="rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
        title={CONTEXT_VARS.find((v) => v.name === cmp.varName)?.description}
      >
        {CONTEXT_VARS.map((v) => (
          <option key={v.name} value={v.name}>
            {v.name}
          </option>
        ))}
      </select>
      <select
        value={cmp.op}
        onChange={(e) => onChange({ ...cmp, op: e.target.value })}
        className="rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
      >
        {OPERATORS_BY_TYPE[varType(cmp.varName)].map((o) => (
          <option key={o.op} value={o.op}>
            {o.label}
          </option>
        ))}
      </select>
      <ValueInput cmp={cmp} onChange={(value) => onChange({ ...cmp, value })} />
      {canRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="text-neutral-500 hover:text-category-error mt-1"
          aria-label="Remove clause"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      ) : null}
    </div>
  );
}

// ─── Type-aware value input ──────────────────────────────────────────────────

function ValueInput({
  cmp,
  onChange,
}: {
  cmp: Comparison;
  onChange: (value: string) => void;
}) {
  const t = varType(cmp.varName);
  const opts = varOptions(cmp.varName);

  // "in" + "regex"/"starts_with"/"ends_with" always take free-form text.
  if (cmp.op === "in") {
    return (
      <input
        type="text"
        value={cmp.value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="FR, DE, BE — comma separated"
        className="flex-1 min-w-0 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
      />
    );
  }
  if (cmp.op === "regex") {
    return (
      <input
        type="text"
        value={cmp.value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="^[A-Z]{2}$"
        className="flex-1 min-w-0 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs font-mono"
      />
    );
  }
  if (cmp.op === "starts_with" || cmp.op === "ends_with") {
    return (
      <input
        type="text"
        value={cmp.value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 min-w-0 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
      />
    );
  }

  if (t === "boolean") {
    return (
      <select
        value={cmp.value === "" ? "true" : cmp.value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 min-w-0 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
      >
        <option value="true">True</option>
        <option value="false">False</option>
      </select>
    );
  }

  if (t === "timestamp") {
    // Convert ISO string ↔ datetime-local format (the input wants
    // "yyyy-MM-ddThh:mm" without timezone). Strip seconds + timezone for
    // display; on save we keep what the user typed (parsed back to ISO).
    return (
      <input
        type="datetime-local"
        value={isoToLocalInput(cmp.value)}
        onChange={(e) => onChange(localInputToIso(e.target.value))}
        className="flex-1 min-w-0 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
      />
    );
  }

  if (opts && opts.length > 0) {
    // Closed enum — render a select. Allow blank as the initial state.
    return (
      <select
        value={cmp.value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 min-w-0 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
      >
        <option value="">— choose —</option>
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  if (t === "number") {
    const hint = varUnitHint(cmp.varName);
    if (!hint) {
      return (
        <input
          type="number"
          value={cmp.value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
        />
      );
    }
    return (
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <input
          type="number"
          value={cmp.value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
        />
        <span className="text-[10px] leading-tight text-neutral-500">
          {hint}
        </span>
      </div>
    );
  }

  return (
    <input
      type="text"
      value={cmp.value}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 min-w-0 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
    />
  );
}

